'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import Script from 'next/script'

import { bing, consentDefault, google, linkedin, meta, pixelsEnabled, tiktok } from '@/config/analytics'

/**
 * Loads the ad-platform browser pixels directly (no tag manager). Each pixel only renders when its
 * id is configured.
 *
 * **All four are told the same consent posture, in each platform's own dialect**, from the single
 * `consentDefault` in `src/config/analytics.ts` — granted while the site has no CMP. They do not
 * share a mechanism: Google takes seven Consent Mode signals, Microsoft one `ad_storage` push that
 * has to reach the queue before `pageLoad`, Meta a data-processing option plus a consent grant,
 * TikTok an explicit cookie enable. Three of the four run cookieless or restricted if left to
 * their own defaults, which is why saying nothing is not the same as saying yes.
 *
 * Conversions are fired from the conversion handlers via `firePixelConversion` (not here); these
 * scripts bootstrap each pixel and record the base PageView, which is what the snippets are
 * designed to do and the only thing that fires reliably on a cold load.
 *
 * **Pageviews carry no shared event id, and that is deliberate.** A dedup id only earns its keep
 * where a server-side counterpart exists to dedup against, and no destination is routed from
 * `$pageview` — the browser pixel is the whole of pageview tracking. Attaching PostHog's uuid was
 * tried and cost the initial pageview three separate ways: a listener registered from a React
 * effect runs after posthog has already captured it, module state is not shared between the
 * `instrumentation-client` entry and the app chunks, and firing before the snippet exists reaches
 * no global at all. A pageview is not a conversion; double-counting one costs nothing in conversion
 * reporting, while losing the ad click's landing costs an audience.
 *
 * `PixelPageviews` covers client-side route changes, which these snippets cannot see.
 */
export function AdPixels() {
  if (!pixelsEnabled) {
    return null
  }

  return (
    <>
      {/*
        `dataProcessingOptions` set to an empty array before `init` is Meta's switch for full
        processing: it turns off Limited Data Use, which Meta otherwise applies on its own for
        some visitors and which strips a conversion down to the point of being unusable for
        attribution. `consent grant` is belt-and-braces — nothing here ever revokes — but it says
        the posture out loud next to the platforms where it has to be said explicitly.
      */}
      {meta.pixelId && (
        <Script id="meta-pixel" strategy="afterInteractive">
          {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('dataProcessingOptions',[]);fbq('consent','${consentDefault === 'granted' ? 'grant' : 'revoke'}');fbq('init','${meta.pixelId}');fbq('track','PageView');`}
        </Script>
      )}

      {google.adsId && (
        <>
          {/*
            All seven Consent Mode v2 signals, not the four the ad products strictly read: a
            partial declaration leaves the rest at Google's own default, which is the opposite of
            stating a posture. `ads_data_redaction` off and `url_passthrough` on are the two
            adjuncts that matter when a browser blocks the cookie anyway — the first stops Google
            stripping the click id from what it does receive, the second carries `gclid` in the URL
            instead.
          */}
          <Script id="google-consent-default" strategy="afterInteractive">
            {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('consent','default',{ad_storage:'${consentDefault}',ad_user_data:'${consentDefault}',ad_personalization:'${consentDefault}',analytics_storage:'${consentDefault}',functionality_storage:'${consentDefault}',personalization_storage:'${consentDefault}',security_storage:'granted'});gtag('set','ads_data_redaction',${consentDefault === 'granted' ? 'false' : 'true'});gtag('set','url_passthrough',true);`}
          </Script>
          <Script
            id="google-ads-src"
            src={`https://www.googletagmanager.com/gtag/js?id=${google.adsId}`}
            strategy="afterInteractive"
          />
          <Script id="google-ads-init" strategy="afterInteractive">
            {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('js',new Date());gtag('config','${google.adsId}');`}
          </Script>
        </>
      )}

      {/* Bing, TikTok and LinkedIn load `lazyOnload`: they exist for remarketing audiences, which
          survive a two-second delay, while `afterInteractive` would queue them right after
          hydration — inside the main-thread window Lighthouse scores as blocking time. Meta and
          Google stay `afterInteractive` because they attribute the ad click that landed the visit. */}
      {/*
        The consent push comes first, inside the same script, and that ordering is the point:
        the bootstrap replays whatever is already sitting in `uetq`, so a signal queued here
        reaches Microsoft ahead of `pageLoad`.

        Measured on 2026-08-07, before this existed: every UET request left as `asc=D`, and the
        tag opened with `evt=consent&asc=D&src=enforced` — Microsoft enforcing a denied state of
        its own because the site offered none, which is what it does to EEA traffic that stays
        silent. `pageLoad` went out denied along with everything after it. Pushing
        `consent/default` flipped the same tag to `asc=G` with `src=default` on the next request.

        Google has been given a default all along; this tells the other platform the same thing,
        from the same `consentDefault`, so one variable moves both.
      */}
      {bing.uetTagId && (
        <Script id="bing-uet" strategy="lazyOnload">
          {`window.uetq=window.uetq||[];window.uetq.push('consent','default',{ad_storage:'${consentDefault}'});(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:'${bing.uetTagId}',enableAutoSpaTracking:true};o.q=w[u],w[u]=new UET(o),w[u].push('pageLoad')},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=='loaded'&&s!=='complete'||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,'script','//bat.bing.com/bat.js','uetq');`}
        </Script>
      )}

      {/*
        `enableCookie` before `page`: TikTok's pixel will run cookieless if it is never told
        otherwise, and a cookieless TikTok event cannot be tied to the click that produced it.
        `grantConsent` is the counterpart to a `holdConsent` this snippet never calls, so it is
        only here to make the posture explicit alongside the other three platforms.
      */}
      {tiktok.pixelId && (
        <Script id="tiktok-pixel" strategy="lazyOnload">
          {`!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie','holdConsent','revokeConsent','grantConsent'];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r='https://analytics.tiktok.com/i18n/pixel/events.js',o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var s=d.createElement('script');s.type='text/javascript';s.async=!0;s.src=r+'?sdkid='+e+'&lib='+t;var a=d.getElementsByTagName('script')[0];a.parentNode.insertBefore(s,a)};ttq.load('${tiktok.pixelId}');${consentDefault === 'granted' ? 'ttq.enableCookie();ttq.grantConsent();' : ''}ttq.page()}(window,document,'ttq');`}
        </Script>
      )}

      {linkedin.partnerId && (
        <>
          <Script id="linkedin-partner" strategy="lazyOnload">
            {`window._linkedin_partner_id='${linkedin.partnerId}';window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push('${linkedin.partnerId}');`}
          </Script>
          <Script id="linkedin-insight" strategy="lazyOnload">
            {`(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName('script')[0];var b=document.createElement('script');b.type='text/javascript';b.async=true;b.src='https://snap.licdn.com/li.lms-analytics/insight.min.js';s.parentNode.insertBefore(b,s)})(window.lintrk);`}
          </Script>
        </>
      )}
    </>
  )
}
